import HeadMetadata from 'components/shared/HeadMetadata';
import { useI18n } from 'hooks/useI18n';
import { usePageTrack } from 'hooks/usePageTrack';
import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Script from 'next/script';
import WPService from 'services/wp.service';
import { APP_URLS } from 'utils/app-urls';
import { avoidRateLimit } from 'utils/rate-limit';
import { WP_REST_API_Post, WP_REST_API_Posts } from 'wp-types';
import SuccessStories from 'components/marketingSite/successStories/SuccessStories';
import {
  FEATURED_POST_ID,
  FEATURED_POST_SLUG,
  SUCCESS_STORIES_BLOG_CATEGORY_ID
} from 'components/marketingSite/successStories/constants';

type BlogPageProps = {
  posts?: WP_REST_API_Posts;
  featuredPost?: WP_REST_API_Post;
  totalPosts: number;
  totalPages: number;
};

const SuccessStoriesPage: React.FC<BlogPageProps> = (props) => {
  const { t } = useI18n('success-stories');

  usePageTrack('Website - Page Loaded');

  return (
    <>
      <HeadMetadata
        title={t('pageTitle')}
        desc={t('pageDescription')}
        imgUrl={'' /* getPublicPath('/images/card_blog.png') */}
        url={APP_URLS.blog}
        indexable={true}
      />

      <Script
        id="blogMetadata"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "${t('pageTitle')}",
            "url": "https://www.getmanfred.com/blog",
            "description": "${t('pageDescription')}",
            "publisher": {
              "@type": "Organization",
              "name": "Manfred",
              "logo": {
                "@type": "ImageObject",
                "url": "https://media-asgard.s3.eu-west-1.amazonaws.com/manfred-media/logo_manfred_color.svg?v=38732000",
                "width": 200,
                "height": 50
              }
            }
          }`
        }}
      />

      <SuccessStories {...props} currentPage={0} />
    </>
  );
};

export default SuccessStoriesPage;

export const getStaticProps: GetStaticProps<BlogPageProps> = async ({
  locale
}) => {
  await avoidRateLimit();
  const responsePosts = await WPService.getPosts({
    locale: locale || 'es',
    category: SUCCESS_STORIES_BLOG_CATEGORY_ID,
    exclude: FEATURED_POST_ID
  });

  const featuredPost = await WPService.getPost(
    FEATURED_POST_SLUG,
    locale || 'es'
  );

  return {
    props: {
      ...(await serverSideTranslations(locale || '', [
        'common',
        'topbar',
        'success-stories',
        'footer'
      ])),
      posts: responsePosts?.data,
      featuredPost: featuredPost || null,
      totalPosts: Number(responsePosts?.headers.get('x-wp-total') || ''),
      totalPages: Number(responsePosts?.headers.get('x-wp-totalpages') || '')
    },
    revalidate: 10
  };
};
